import React, { useEffect, useMemo, useRef } from "react";
import { AnimatePresence, motion, useAnimationControls } from "framer-motion";
import { LAYOUT, PLAIN_STONE, STONE_COLORS, asset } from "./gameUtils";

// The river stage: painted backdrop, drifting water highlights + floating
// leaves, an ambient dragonfly, the stepping stones as they rise, the current
// critter walking / hopping across, and the cheering crowd on the far bank.
// Everything is placed in unit coordinates (see LAYOUT / STONE_SLOTS).

const pct = (unit) => ({ left: `${unit.x * 100}%`, top: `${unit.y * 100}%` });

// ---------------------------------------------------------------------------
// Ambient water effects
// ---------------------------------------------------------------------------

const STREAKS = Array.from({ length: 12 }, (_, i) => {
  const band = LAYOUT.riverBand;
  const t = ((i * 211) % 100) / 100;
  return {
    id: i,
    left: `${(band.start + t * (band.end - band.start)) * 100}%`,
    duration: `${5 + ((i * 137) % 45) / 10}s`,
    delay: `${(((i * 173) % 100) / 100) * 4.5}s`,
    height: `${26 + ((i * 97) % 26)}px`,
  };
});

const WaterFX = () => (
  <div className="cc-water-fx" aria-hidden="true">
    {STREAKS.map((streak) => (
      <span
        key={streak.id}
        className="cc-streak"
        style={{
          left: streak.left,
          height: streak.height,
          "--cc-dur": streak.duration,
          "--cc-delay": streak.delay,
        }}
      />
    ))}
    <img className="cc-leaf cc-leaf-1" src={asset("cc_leaf")} alt="" draggable="false" />
    <img className="cc-leaf cc-leaf-2" src={asset("cc_leaf")} alt="" draggable="false" />
    <img className="cc-dragonfly" src={asset("cc_dragonfly")} alt="" draggable="false" />
  </div>
);

// ---------------------------------------------------------------------------
// Splash + sparkle bursts
// ---------------------------------------------------------------------------

const DROPS = Array.from({ length: 9 }, (_, i) => {
  const spread = Math.PI * 1.1;
  const start = -Math.PI / 2 - spread / 2;
  const angle = start + (i / 8) * spread;
  return { id: i, dx: Math.cos(angle) * 38, dy: Math.sin(angle) * 38 - 10 };
});

const SplashBurst = () => (
  <motion.div className="cc-splash" aria-hidden="true">
    {[0, 1].map((ring) => (
      <motion.span
        key={ring}
        className="cc-ripple"
        initial={{ scale: 0.35, opacity: ring === 0 ? 0.85 : 0.55 }}
        animate={{ scale: 3.2, opacity: 0 }}
        transition={{ duration: 0.75, ease: "easeOut" }}
      />
    ))}
    {DROPS.map((drop) => (
      <motion.span
        key={drop.id}
        className="cc-drop"
        initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
        animate={{ x: drop.dx, y: drop.dy, opacity: 0, scale: 0.5 }}
        transition={{ duration: 0.55, ease: "easeOut" }}
      />
    ))}
  </motion.div>
);

const SPARKLES = Array.from({ length: 8 }, (_, i) => {
  const angle = (i / 8) * Math.PI * 2;
  return { id: i, dx: Math.cos(angle) * 46, dy: Math.sin(angle) * 46 };
});

const SparkleBurst = () => (
  <motion.div className="cc-sparkle" aria-hidden="true">
    {SPARKLES.map((sparkle) => (
      <motion.span
        key={sparkle.id}
        className="cc-sparkle-bit"
        initial={{ x: 0, y: 0, scale: 0.2, opacity: 1 }}
        animate={{ x: sparkle.dx, y: sparkle.dy, scale: 1, opacity: 0 }}
        transition={{ duration: 0.85, ease: "easeOut" }}
      />
    ))}
  </motion.div>
);

// ---------------------------------------------------------------------------
// Stones
// ---------------------------------------------------------------------------

const Stone = ({ stone, side, bounce }) => {
  const controls = useAnimationControls();
  const first = useRef(true);
  useEffect(() => {
    // Rise out of the water with a scale-in.
    controls.start({ scale: 1, opacity: 1, transition: { type: "spring", stiffness: 240, damping: 15 } });
  }, [controls]);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    // Wobble underfoot on a landing / recount beat.
    controls.start({ scale: [1, 1.16, 1], transition: { duration: 0.4, ease: "easeOut" } });
  }, [bounce, controls]);

  const src = stone.color ? STONE_COLORS[stone.color].asset : PLAIN_STONE;
  return (
    <motion.img
      className="cc-stone"
      src={src}
      alt=""
      draggable="false"
      style={{ ...pct(stone.unit), width: side, height: side, marginLeft: -side / 2, marginTop: -side / 2 }}
      initial={{ scale: 1.6, opacity: 0 }}
      animate={controls}
      exit={{ scale: 0.3, opacity: 0 }}
    />
  );
};

const StonesLayer = ({ stones, stoneBounce, side }) => (
  <AnimatePresence>
    {stones.map((stone) => (
      <Stone key={stone.id} stone={stone} side={side} bounce={stoneBounce[stone.id] || 0} />
    ))}
  </AnimatePresence>
);

// ---------------------------------------------------------------------------
// Far-bank friends
// ---------------------------------------------------------------------------

const BobbingFriend = ({ critter, index, side, cheer }) => {
  const controls = useAnimationControls();
  const spot = LAYOUT.friendSpots[index % LAYOUT.friendSpots.length];

  useEffect(() => {
    let active = true;
    (async () => {
      await controls.start({ opacity: 1, scale: 1, transition: { type: "spring", stiffness: 220, damping: 16 } });
      if (!active) return;
      controls.start({
        y: [0, -4, 0],
        transition: { duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: index * 0.3 },
      });
    })();
    return () => {
      active = false;
    };
  }, [controls, index]);

  useEffect(() => {
    if (!cheer) return;
    (async () => {
      await controls.start({
        y: [0, -14, 0],
        scale: [1, 1.12, 1],
        transition: { duration: 0.5, ease: "easeOut", delay: index * 0.09 },
      });
      // Resume the gentle idle bob afterwards.
      controls.start({ y: [0, -4, 0], transition: { duration: 1.8, repeat: Infinity, ease: "easeInOut" } });
    })();
  }, [cheer, controls, index]);

  return (
    <motion.img
      className="cc-friend"
      src={critter.happyAsset}
      alt={critter.name}
      draggable="false"
      style={{ ...pct(spot), width: side, height: side, marginLeft: -side / 2, marginTop: -side * 0.88 }}
      initial={{ scale: 0.4, opacity: 0 }}
      animate={controls}
    />
  );
};

// ---------------------------------------------------------------------------
// Current critter actor (driven imperatively by the page's choreography)
// ---------------------------------------------------------------------------

const CritterActor = ({ critter, happy, side, wrapperControls, bodyControls, shadowControls }) => (
  <motion.div
    className="cc-actor"
    style={{ width: side, height: side }}
    animate={wrapperControls}
    initial={false}
  >
    <motion.span
      className="cc-actor-shadow"
      style={{ width: side * 0.7, height: side * 0.16, marginLeft: -side * 0.35, bottom: -side * 0.08 }}
      animate={shadowControls}
      initial={{ scaleX: 1, opacity: 0.18 }}
    />
    <motion.div className="cc-actor-body" style={{ width: side, height: side }} animate={bodyControls} initial={false}>
      <img
        className="cc-actor-sprite"
        src={critter.waitingAsset}
        alt={critter.name}
        draggable="false"
        style={{ opacity: happy ? 0 : 1 }}
      />
      <img
        className="cc-actor-sprite"
        src={critter.happyAsset}
        alt=""
        draggable="false"
        style={{ opacity: happy ? 1 : 0, transform: `scale(${happy ? 1 : 0.94})` }}
      />
    </motion.div>
  </motion.div>
);

// ---------------------------------------------------------------------------
// Speech bubble
// ---------------------------------------------------------------------------

const SpeechBubble = ({ text, unit }) => (
  <motion.div
    className="cc-bubble"
    style={{ left: `${(unit.x + 0.14) * 100}%`, top: `${(unit.y - 0.24) * 100}%` }}
    initial={{ scale: 0.7, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    exit={{ scale: 0.7, opacity: 0 }}
    transition={{ type: "spring", stiffness: 320, damping: 22 }}
  >
    {text}
    <span className="cc-bubble-tail" />
  </motion.div>
);

// ---------------------------------------------------------------------------
// Scene
// ---------------------------------------------------------------------------

const RiverScene = ({
  dims,
  stones,
  stoneBounce,
  friends,
  cheerTick,
  splash,
  sparkleTick,
  actor,
  celebrating,
  mood = "noon",
}) => {
  const side = useMemo(() => {
    const base = Math.min(dims.w || 0, dims.h || 0);
    return base * 0.24;
  }, [dims.w, dims.h]);
  const stoneSide = useMemo(() => Math.min(dims.w || 0, dims.h || 0) * 0.135, [dims.w, dims.h]);

  return (
    <div className={`cc-scene ${celebrating ? "is-celebrating" : ""}`}>
      <img className="cc-backdrop" src={asset("cc_river_scene")} alt="A woodland river" draggable="false" />
      {mood && mood !== "noon" && <div className={`cc-mood cc-mood--${mood}`} aria-hidden="true" />}
      <WaterFX />

      <StonesLayer stones={stones} stoneBounce={stoneBounce} side={stoneSide} />

      {friends.map((critter, index) => (
        <BobbingFriend key={critter.id} critter={critter} index={index} side={side * 0.82} cheer={cheerTick} />
      ))}

      {actor.critter && (
        <CritterActor
          critter={actor.critter}
          happy={actor.happy}
          side={side}
          wrapperControls={actor.wrapperControls}
          bodyControls={actor.bodyControls}
          shadowControls={actor.shadowControls}
        />
      )}

      <AnimatePresence>
        {actor.bubble && actor.critter && (
          <SpeechBubble key={actor.bubble} text={actor.bubble} unit={actor.bubbleUnit} />
        )}
      </AnimatePresence>

      {splash && (
        <div key={`splash-${splash.id}`} className="cc-burst-anchor" style={pct(splash.unit)}>
          <SplashBurst />
        </div>
      )}
      {sparkleTick > 0 && (
        <div key={`sparkle-${sparkleTick}`} className="cc-burst-anchor" style={pct(LAYOUT.arrivalSpot)}>
          <SparkleBurst />
        </div>
      )}

      {celebrating && <div className="cc-celebrate-glow" aria-hidden="true" />}
    </div>
  );
};

export default RiverScene;
